import React, { ReactElement } from "react";
import PropTypes from 'prop-types';

interface ButtonRowProps extends React.HTMLAttributes<HTMLElement> {
    // Baseline arguments
    alignLeft?: boolean;
    children?: React.ReactNode;
    className?: string;
}

// Making a TOTALLY 100% accurate mimic interface of the button so that React is happy with us assigning a class to it.
interface ButtonMimic {
    className?: string;
}

/**
 * A row wrapper for buttons. Children buttons will be given a specific button-row class.
 * Note: Buttons passed in wrapped in a React fragment will also be given the class.
 * 
 * ## _
 * ### Arguments
 * @param {boolean} alignLeft - A flag to align the button row to the left.
 * @param {React.ReactNode} children - The contents inside the button row. Each will have the 'm-button-row__button' class applied.
 * @param {string} className - CSS classes to apply to the button row.
 */
class ButtonRow extends React.Component<ButtonRowProps> {
    static propTypes = {
        alignLeft: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
    }

    static defaultProps = {
        alignLeft: false,
        className: "",
    }

    render() {
        const { alignLeft, children, className, ...rest } = this.props;

        let classes = 'm-button-row'

        if(alignLeft) classes += ` m-button-row--left-aligned`
        if(className) classes += ` ${className}`

        // Wrap everything in ButtonRow and give every button the 'm-button-row__button' class.
        return (
            <div className={classes} {...rest}>
                {React.Children.map(children, (child) => {
                    // If we're working with a fragment with Buttons inside, iterate over all the children of IT.
                    if (React.isValidElement(child) && child.type == React.Fragment) {
                        return React.Children.map(child.props.children, (grandchild) => {
                            if (React.isValidElement<ButtonMimic>(grandchild)) {
                                return React.cloneElement(grandchild, {
                                    className: `${grandchild.props.className} m-button-row__button`,
                                });
                            }
                            return grandchild;
                        });
                    }
                    // Otherwise, double check that it can recieve a class, then give it the class.
                    else if (React.isValidElement<ButtonMimic>(child)) {
                        return React.cloneElement(child as ReactElement, {
                            className: `${child.props.className} m-button-row__button`
                        });
                    }
                    return child;
                })}
            </div>
        );
    }
}

export default ButtonRow;